import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../../utils/logger';
import { Card } from '../../../shared/components';
import { Rule, setupApi } from '../../payroll.api';

type Draft = Record<string, string>;

const toDraft = (rules: Rule[]): Draft =>
  Object.fromEntries(
    rules.map((r) => [r.code, r.valueType === 'NUMBER' ? (r.valueNum ?? '').toString() : (r.valueText ?? '').toString().slice(0, r.valueType === 'TIME' ? 5 : undefined)])
  );

/** The office rules, grouped, edited in place and saved together. */
export default function RulesTab({ canEdit }: { canEdit: boolean }) {
  const [rules, setRules] = useState<Rule[]>([]);
  const [draft, setDraft] = useState<Draft>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setupApi
      .rules()
      .then((r) => {
        setRules(r);
        setDraft(toDraft(r));
      })
      .catch((e) => setError(logError({ message: 'Could not load the payroll rules', error: e, code: 'PAYROLL_RULES_FAILED' })))
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const out = new Map<string, Rule[]>();
    rules.forEach((r) => out.set(r.groupName || 'General', [...(out.get(r.groupName || 'General') ?? []), r]));
    return [...out.entries()];
  }, [rules]);

  const original = useMemo(() => toDraft(rules), [rules]);
  const changed = rules.filter((r) => (draft[r.code] ?? '') !== (original[r.code] ?? ''));

  const save = async () => {
    const bad = changed.find((r) => r.valueType === 'NUMBER' && draft[r.code] !== '' && Number.isNaN(Number(draft[r.code])));
    if (bad) {
      toast.error(`${bad.label} must be a number.`);
      return;
    }
    setSaving(true);
    try {
      const saved = await setupApi.saveRules(
        changed.map((r) =>
          r.valueType === 'NUMBER'
            ? { code: r.code, valueNum: draft[r.code] === '' ? null : Number(draft[r.code]), valueText: null }
            : { code: r.code, valueNum: null, valueText: draft[r.code] || null }
        )
      );
      setRules(saved);
      setDraft(toDraft(saved));
      toast.success('Rules saved. Unpaid months will recalculate with them.');
    } catch (e) {
      toast.error(logError({ message: 'Could not save the rules', error: e, code: 'PAYROLL_RULES_SAVE_FAILED' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={2}>
      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}
      {groups.map(([group, items]) => (
        <Card key={group} title={group.toUpperCase()}>
          <Stack gap={2}>
            {items.map((r) => {
              const common = {
                size: 'small' as const,
                label: r.label,
                value: draft[r.code] ?? '',
                disabled: !canEdit,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => setDraft((d) => ({ ...d, [r.code]: e.target.value })),
                sx: { minWidth: 240, maxWidth: 360 },
              };
              return (
                <Stack key={r.code} direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'center' }}>
                  {r.valueType === 'CHOICE' ? (
                    <TextField select {...common}>
                      {(r.choices ?? '')
                        .split(',')
                        .map((c) => c.trim())
                        .filter(Boolean)
                        .map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                    </TextField>
                  ) : r.valueType === 'TIME' ? (
                    <TextField type='time' InputLabelProps={{ shrink: true }} {...common} />
                  ) : r.valueType === 'NUMBER' ? (
                    <TextField type='number' inputProps={{ step: 'any' }} {...common} />
                  ) : (
                    <TextField {...common} />
                  )}
                  <Typography variant='body2' color='text.secondary' sx={{ flex: 1 }}>
                    {r.description}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Card>
      ))}
      {canEdit && rules.length > 0 && (
        <Stack direction='row' justifyContent='flex-end' gap={1}>
          <Button disabled={!changed.length || saving} onClick={() => setDraft(original)}>
            Undo changes
          </Button>
          <Button variant='contained' disabled={!changed.length || saving} onClick={save}>
            {saving ? 'Saving…' : `Save${changed.length ? ` (${changed.length})` : ''}`}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
